/**
 * When to apply a new build.
 *
 * A service worker that has finished installing a newer bundle is *waiting*:
 * the page in front of the player is still the old one, and stays the old one
 * until something tells the worker to take over and the page reloads. Left
 * alone, that is "a new build only shows on the second load" — and a session
 * kept open across a protocol bump is refused by the server with nothing on
 * screen to say why.
 *
 * Applying it means a reload, and a reload in the middle of a run or a duel
 * throws the game away. So the rule is the one the owner asked for:
 *
 * - on the tab shell (Play / Versus / You) — nothing to lose — reload at once;
 * - anywhere else (a solo run, the results of one, a duel, the admin panel)
 *   — hold it, and apply it the moment the player is back on the tabs.
 *
 * This class is that rule and nothing else: two booleans and one callback, no
 * DOM and no service-worker API, so `test/pwa/update.test.ts` can drive every
 * ordering directly. `register-sw.ts` is the thin layer that feeds it from
 * the real `virtual:pwa-register`; `App.svelte` feeds it the screen.
 */
export class UpdateCoordinator {
  private waiting = false;
  private safe = false;
  private applied = false;

  /**
   * @param apply Tells the waiting worker to take over and reloads the page.
   *   Called at most once per page life — a reload ends this object anyway,
   *   and a second call while the first is still taking effect would only
   *   race it.
   */
  constructor(private readonly apply: () => void) {}

  /** A newer build has finished installing and is waiting. */
  get isWaiting(): boolean {
    return this.waiting;
  }

  /** The service worker reported a waiting update. */
  notifyWaiting(): void {
    this.waiting = true;
    this.maybeApply();
  }

  /** `true` while a reload would cost the player nothing — the tab shell. */
  setSafeToReload(safe: boolean): void {
    this.safe = safe;
    this.maybeApply();
  }

  private maybeApply(): void {
    if (!this.waiting || !this.safe || this.applied) return;
    this.applied = true;
    this.apply();
  }
}
